import { Fragment, type ReactNode, useEffect, useState } from 'react'

import { t } from '../../../../../common/i18n.js'
import { Language } from '../../../../../common/types/i18n.js'
import { ALL_AUDIT_ACTIONS } from '../../../../../common/types/portal-audit.js'
import { Select } from '../../../../components/forms/select/select.js'
import { TextField } from '../../../../components/forms/text-field/text-field.js'
import {
    Activity,
    Ban,
    BookOpen,
    Check,
    ChevronLeft,
    ChevronRight,
    ClipboardList,
    Coins,
    DoorOpen,
    Download,
    Eye,
    FileText,
    Key,
    Link,
    Palmtree,
    Pencil,
    Plug,
    RefreshCw,
    Scale,
    Settings,
    Shield,
    Theater,
    TimerReset,
    Trash2,
    User,
    UserMinus,
    UserPlus,
    X,
} from '../../../../components/icon/solar-icons.js'
import { PageHeader } from '../../../../components/page-header/page-header.js'
import { PageShell } from '../../../../components/page-shell/page-shell.js'
import { Spinner } from '../../../../components/spinner/spinner.js'
import { portalGet } from '../../utils/api.js'
import { formatUtcTimestamp } from '../../utils/format.js'
import './audit-log-view.css'

// ── Types ──

interface AuditLogEntry {
    id: number
    timestamp_utc: string
    actor_id: number | null
    actor_display_name: string | null
    impersonating_as_id: number | null
    action_type: string
    target_entity_type: string | null
    target_entity_id: number | null
    target_display_name: string | null
    ip_address: string | null
    user_agent: string | null
    metadata: Record<string, unknown> | null
}

export interface AuditLogViewProps {
    lang: Language
    // When true, render content only (no PageShell/PageHeader) — the host view
    // (e.g. the Settings tab) already provides the page chrome.
    embedded?: boolean
}

const PAGE_SIZE = 50

interface ActorOption {
    id: number
    name: string
}

// Turn a raw enum / snake_case token into a readable fallback so an action or
// entity type without an explicit i18n entry never renders as SCREAMING_CASE
// (the composed "<Entity> <verb>" sentence and the filter options rely on it).
const humanize = (value: string): string =>
    value
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/^\w/, (c) => c.toUpperCase())

const ACTION_ICONS: Record<string, ReactNode> = {
    LOGIN_SUCCESS: <Key size={12} />,
    LOGIN_FAILED: <X size={12} />,
    LOGIN_DENIED_DOMAIN: <Ban size={12} />,
    LOGOUT: <DoorOpen size={12} />,
    LOGIN_AS: <Theater size={12} />,
    LOGIN_AS_END: <Theater size={12} />,
    EMPLOYEE_CREATED: <User size={12} />,
    EMPLOYEE_UPDATED: <Pencil size={12} />,
    EMPLOYEE_STATUS_CHANGED: <RefreshCw size={12} />,
    EMPLOYEE_OFFBOARDED: <DoorOpen size={12} />,
    COMPENSATION_CREATED: <Coins size={12} />,
    COMPENSATION_UPDATED: <Coins size={12} />,
    COMPENSATION_DELETED: <Coins size={12} />,
    COMPENSATION_REVEALED: <Eye size={12} />,
    COMPENSATION_ACTIVATED: <Coins size={12} />,
    EMPLOYMENT_RECORD_DELETED: <Trash2 size={12} />,
    TIME_OFF_REQUESTED: <Palmtree size={12} />,
    TIME_OFF_APPROVED: <Check size={12} />,
    TIME_OFF_REJECTED: <X size={12} />,
    TIME_OFF_CANCELLED: <Ban size={12} />,
    TIME_OFF_CANCEL_REQUESTED: <Ban size={12} />,
    TIME_OFF_CANCEL_REJECTED: <X size={12} />,
    LEAVE_LEDGER_ADJUSTED: <Scale size={12} />,
    LEAVE_ACCRUED: <Palmtree size={12} />,
    LEAVE_EXPIRED: <TimerReset size={12} />,
    POLICY_CREATED: <ClipboardList size={12} />,
    POLICY_UPDATED: <ClipboardList size={12} />,
    SETTINGS_UPDATED: <Settings size={12} />,
    EXPORT_REPORT: <Download size={12} />,
    EXPORT_EMPLOYEE_LIST: <Download size={12} />,
    ROLE_CREATED: <Shield size={12} />,
    ROLE_UPDATED: <Shield size={12} />,
    ROLE_DELETED: <X size={12} />,
    ROLE_MEMBER_ADDED: <UserPlus size={12} />,
    ROLE_MEMBER_REMOVED: <UserMinus size={12} />,
    EMPLOYEE_CONTRACT_CREATED: <FileText size={12} />,
    EMPLOYEE_CONTRACT_UPDATED: <Pencil size={12} />,
    EMPLOYEE_CONTRACT_PROBATION_EXTENDED: <Pencil size={12} />,
    EMERGENCY_CONTACT_ADDED: <UserPlus size={12} />,
    EMERGENCY_CONTACT_UPDATED: <Pencil size={12} />,
    EMERGENCY_CONTACT_REMOVED: <UserMinus size={12} />,
    DOCUMENT_UPLOADED: <FileText size={12} />,
    DOCUMENT_UPDATED: <Pencil size={12} />,
    DOCUMENT_DELETED: <Trash2 size={12} />,
    EXPERIENCE_ADDED: <BookOpen size={12} />,
    EXPERIENCE_UPDATED: <Pencil size={12} />,
    EXPERIENCE_REMOVED: <Trash2 size={12} />,
    WORKFLOW_TASK_CREATED: <ClipboardList size={12} />,
    WORKFLOW_TASK_COMPLETED: <Check size={12} />,
    WORKFLOW_TASK_DISMISSED: <Ban size={12} />,
    INTEGRATION_CONNECTED: <Plug size={12} />,
    INTEGRATION_DISCONNECTED: <Link size={12} />,
}

// Entity-type label (i18n, with a humanize() fallback for unmapped types).
const getEntityTypeLabel = (type: string, lang: Language): string =>
    t.portal.audit.entityTypes[type]?.(lang) ?? humanize(type)

// Base action label for the filter dropdown — one stable entry per action type.
const getActionTypeOptionLabel = (action: string, lang: Language): string =>
    t.portal.audit.actions[action]?.(lang) ?? humanize(action)

// Row-aware label. SETTINGS_UPDATED is a catch-all the backend uses for ~30
// distinct settings mutations, so compose "<Entity> <verb>" from
// target_entity_type + metadata.action (e.g. "Team created") rather than a flat
// "Settings updated". Everything else uses its per-action label.
const getActionLabel = (entry: AuditLogEntry, lang: Language): string => {
    if (entry.action_type === 'SETTINGS_UPDATED') {
        const verbKey = typeof entry.metadata?.action === 'string' ? entry.metadata.action : ''
        const verb = t.portal.audit.settingsVerbs[verbKey]?.(lang)
        if (entry.target_entity_type && verb) {
            return `${getEntityTypeLabel(entry.target_entity_type, lang)} ${verb}`
        }
    }
    return getActionTypeOptionLabel(entry.action_type, lang)
}

const getActionCategoryClass = (action: string): string => {
    if (action.startsWith('LOGIN') || action === 'LOGOUT') return 'auth'
    if (action.startsWith('TIME_OFF') || action.startsWith('LEAVE')) return 'leave'
    if (action.startsWith('COMPENSATION')) return 'compensation'
    if (action.startsWith('EMERGENCY_CONTACT') || action.startsWith('DOCUMENT')) return 'employee'
    if (action.startsWith('EXPERIENCE')) return 'employee'
    if (action.startsWith('EMPLOYEE') || action.startsWith('EMPLOYMENT')) return 'employee'
    if (action.startsWith('WORKFLOW_TASK')) return 'workflow'
    if (action.startsWith('INTEGRATION')) return 'integration'
    if (action.startsWith('ROLE') || action.startsWith('POLICY') || action === 'SETTINGS_UPDATED')
        return 'settings'
    if (action.startsWith('EXPORT')) return 'export'
    return 'default'
}

export const AuditLogView = ({ lang, embedded }: AuditLogViewProps) => {
    const [entries, setEntries] = useState<AuditLogEntry[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [isLoading, setIsLoading] = useState(true)

    // Filters
    const [filterAction, setFilterAction] = useState('')
    const [filterTargetType, setFilterTargetType] = useState('')
    const [filterFromDate, setFilterFromDate] = useState('')
    const [filterToDate, setFilterToDate] = useState('')
    const [filterActorId, setFilterActorId] = useState('')
    const [filterActorKind, setFilterActorKind] = useState('')
    const [actors, setActors] = useState<ActorOption[]>([])
    const [entityTypes, setEntityTypes] = useState<string[]>([])

    // Detail panel
    const [expandedId, setExpandedId] = useState<number | null>(null)

    const tA = t.portal.audit

    // Load distinct actors and target entity types for the filter dropdowns,
    // derived from recent data (same self-healing "last 1000 rows" approach as
    // the actor list — new entity types appear automatically, the defunct
    // 'group' value never does because nothing writes it anymore).
    useEffect(() => {
        const loadFilterOptions = async () => {
            const result = await portalGet<{ entries: AuditLogEntry[] }>(
                '/api/portal/audit-logs?limit=1000',
            )
            if (result.ok) {
                const actorMap = new Map<number, string>()
                const typeSet = new Set<string>()
                for (const e of result.data.entries) {
                    if (e.actor_id && e.actor_display_name) {
                        actorMap.set(e.actor_id, e.actor_display_name)
                    }
                    if (e.target_entity_type) typeSet.add(e.target_entity_type)
                }
                setActors(
                    [...actorMap.entries()]
                        .map(([id, name]) => ({ id, name }))
                        .sort((a, b) => a.name.localeCompare(b.name)),
                )
                setEntityTypes([...typeSet].sort())
            }
        }
        void loadFilterOptions()
    }, [])

    // Load data
    useEffect(() => {
        const load = async () => {
            setIsLoading(true)
            const params = new URLSearchParams()
            params.set('page', String(page))
            params.set('limit', String(PAGE_SIZE))
            if (filterAction) params.set('action_type', filterAction)
            if (filterTargetType) params.set('target_entity_type', filterTargetType)
            if (filterFromDate) params.set('from_date', filterFromDate)
            if (filterToDate) params.set('to_date', filterToDate)
            if (filterActorId) params.set('actor_id', filterActorId)
            if (filterActorKind) params.set('actor_kind', filterActorKind)

            const result = await portalGet<{
                entries: AuditLogEntry[]
                total: number
                page: number
            }>(`/api/portal/audit-logs?${params.toString()}`)

            if (result.ok) {
                setEntries(result.data.entries)
                setTotal(result.data.total)
            }
            setIsLoading(false)
        }

        void load()
    }, [
        page,
        filterAction,
        filterTargetType,
        filterFromDate,
        filterToDate,
        filterActorId,
        filterActorKind,
    ])

    const totalPages = Math.ceil(total / PAGE_SIZE)
    const showingFrom = total > 0 ? (page - 1) * PAGE_SIZE + 1 : 0
    const showingTo = Math.min(page * PAGE_SIZE, total)

    const handleResetFilters = () => {
        setFilterAction('')
        setFilterTargetType('')
        setFilterFromDate('')
        setFilterToDate('')
        setFilterActorId('')
        setFilterActorKind('')
        setPage(1)
    }

    const hasActiveFilters =
        filterAction !== '' ||
        filterTargetType !== '' ||
        filterFromDate !== '' ||
        filterToDate !== '' ||
        filterActorId !== '' ||
        filterActorKind !== ''

    if (isLoading && entries.length === 0) {
        return (
            <div className="portal-audit__loading">
                <Spinner />
            </div>
        )
    }

    const totalBadge = (
        <span className="portal-audit__total-badge">
            {total} {tA.entries(lang)}
        </span>
    )

    const content = (
        <>
            {/* Filters */}
            <div className="portal-audit__filters">
                <Select
                    className="portal-audit__filter-select"
                    size="small"
                    placeholder={tA.allTypes(lang)}
                    value={filterAction}
                    onChange={(value) => {
                        setFilterAction(value)
                        setPage(1)
                    }}
                    options={ALL_AUDIT_ACTIONS.map((action) => ({
                        value: action,
                        label: getActionTypeOptionLabel(action, lang),
                    }))}
                />

                <Select
                    className="portal-audit__filter-select"
                    size="small"
                    placeholder={tA.allActors(lang)}
                    value={filterActorId}
                    onChange={(value) => {
                        setFilterActorId(value)
                        setPage(1)
                    }}
                    options={actors.map((actor) => ({
                        value: String(actor.id),
                        label: actor.name,
                    }))}
                />

                <Select
                    className="portal-audit__filter-select"
                    size="small"
                    placeholder={tA.actorKind(lang)}
                    value={filterActorKind}
                    onChange={(value) => {
                        setFilterActorKind(value)
                        setPage(1)
                    }}
                    options={[
                        { value: 'human', label: tA.human(lang) },
                        { value: 'system', label: tA.system(lang) },
                    ]}
                />

                <Select
                    className="portal-audit__filter-select"
                    size="small"
                    placeholder={tA.allTargets(lang)}
                    value={filterTargetType}
                    onChange={(value) => {
                        setFilterTargetType(value)
                        setPage(1)
                    }}
                    options={entityTypes.map((type) => ({
                        value: type,
                        label: getEntityTypeLabel(type, lang),
                    }))}
                />

                <TextField
                    className="portal-audit__filter-input"
                    size="small"
                    value={filterFromDate}
                    onChange={(value) => {
                        setFilterFromDate(value)
                        setPage(1)
                    }}
                    inputProps={{ type: 'date', title: tA.fromDate(lang) }}
                />

                <TextField
                    className="portal-audit__filter-input"
                    size="small"
                    value={filterToDate}
                    onChange={(value) => {
                        setFilterToDate(value)
                        setPage(1)
                    }}
                    inputProps={{ type: 'date', title: tA.toDate(lang) }}
                />

                {hasActiveFilters && (
                    <button
                        type="button"
                        className="portal-audit__filter-reset"
                        onClick={handleResetFilters}
                    >
                        <X size={14} /> {tA.clear(lang)}
                    </button>
                )}
            </div>

            {/* Table */}
            {entries.length === 0 ? (
                <div className="portal-audit__empty">
                    <div className="portal-audit__empty-icon">
                        <ClipboardList size={32} />
                    </div>
                    <div>{tA.noEntries(lang)}</div>
                </div>
            ) : (
                <>
                    <div className="portal-audit__table-wrap">
                        <table className="portal-audit__table">
                            <thead>
                                <tr>
                                    <th>{tA.date(lang)}</th>
                                    <th>{tA.actor(lang)}</th>
                                    <th>{tA.action(lang)}</th>
                                    <th>{tA.target(lang)}</th>
                                    <th>IP</th>
                                    <th>{tA.details(lang)}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {entries.map((entry) => {
                                    const categoryClass = getActionCategoryClass(entry.action_type)
                                    const isExpanded = expandedId === entry.id

                                    return (
                                        <Fragment key={entry.id}>
                                            <tr
                                                className={
                                                    isExpanded
                                                        ? 'portal-audit__row is-expanded'
                                                        : 'portal-audit__row'
                                                }
                                                onClick={() =>
                                                    setExpandedId(isExpanded ? null : entry.id)
                                                }
                                            >
                                                <td className="portal-audit__date">
                                                    {formatUtcTimestamp(entry.timestamp_utc, lang)}
                                                </td>
                                                <td>
                                                    <span
                                                        className={`portal-audit__actor${!entry.actor_display_name ? ' portal-audit__actor--system' : ''}`}
                                                    >
                                                        {entry.actor_display_name ??
                                                            tA.system(lang)}
                                                    </span>
                                                    {entry.impersonating_as_id && (
                                                        <span className="portal-audit__impersonation-badge">
                                                            <Theater size={12} />
                                                        </span>
                                                    )}
                                                    {entry.metadata?.automated === true && (
                                                        <span
                                                            className="portal-audit__automated-badge"
                                                            title={tA.automated(lang)}
                                                        >
                                                            <Activity size={12} />{' '}
                                                            {tA.automated(lang)}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span
                                                        className={`portal-audit__action-badge portal-audit__action-badge--${categoryClass}`}
                                                    >
                                                        {ACTION_ICONS[entry.action_type]}{' '}
                                                        {getActionLabel(entry, lang)}
                                                    </span>
                                                </td>
                                                <td>
                                                    {entry.target_display_name && (
                                                        <span className="portal-audit__target-name">
                                                            {entry.target_display_name}
                                                        </span>
                                                    )}
                                                    {entry.target_entity_type && (
                                                        <span className="portal-audit__target-type">
                                                            {getEntityTypeLabel(
                                                                entry.target_entity_type,
                                                                lang,
                                                            )}
                                                            {entry.target_entity_id
                                                                ? ` #${entry.target_entity_id}`
                                                                : ''}
                                                        </span>
                                                    )}
                                                    {!entry.target_entity_type &&
                                                        !entry.target_display_name &&
                                                        '—'}
                                                </td>
                                                <td className="portal-audit__ip">
                                                    {entry.ip_address ?? '—'}
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="portal-audit__expand-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            setExpandedId(
                                                                isExpanded ? null : entry.id,
                                                            )
                                                        }}
                                                    >
                                                        {isExpanded ? '▼' : '▶'}
                                                    </button>
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                                <tr className="portal-audit__detail-row">
                                                    <td colSpan={6}>
                                                        <div className="portal-audit__detail-panel">
                                                            <div className="portal-audit__detail-grid">
                                                                <div className="portal-audit__detail-item">
                                                                    <span className="portal-audit__detail-label">
                                                                        {tA.userAgent(lang)}
                                                                    </span>
                                                                    <span className="portal-audit__detail-value portal-audit__detail-value--ua">
                                                                        {entry.user_agent ?? '—'}
                                                                    </span>
                                                                </div>
                                                                {entry.impersonating_as_id && (
                                                                    <div className="portal-audit__detail-item">
                                                                        <span className="portal-audit__detail-label">
                                                                            {tA.impersonatingUser(
                                                                                lang,
                                                                            )}
                                                                        </span>
                                                                        <span className="portal-audit__detail-value">
                                                                            #
                                                                            {
                                                                                entry.impersonating_as_id
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {entry.metadata && (
                                                                    <div className="portal-audit__detail-item portal-audit__detail-item--full">
                                                                        <span className="portal-audit__detail-label">
                                                                            {tA.metadata(lang)}
                                                                        </span>
                                                                        <pre className="portal-audit__detail-json">
                                                                            {JSON.stringify(
                                                                                entry.metadata,
                                                                                null,
                                                                                2,
                                                                            )}
                                                                        </pre>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="portal-audit__pagination">
                            <div className="portal-audit__pagination-info">
                                {tA.showing(showingFrom, showingTo, total)(lang)}
                            </div>
                            <div className="portal-audit__pagination-controls">
                                <button
                                    type="button"
                                    className="portal-audit__page-btn"
                                    disabled={page <= 1}
                                    onClick={() => setPage(page - 1)}
                                >
                                    <ChevronLeft size={14} />
                                    {tA.prev(lang)}
                                </button>
                                <span className="portal-audit__page-label">
                                    {tA.page(page, totalPages)(lang)}
                                </span>
                                <button
                                    type="button"
                                    className="portal-audit__page-btn"
                                    disabled={page >= totalPages}
                                    onClick={() => setPage(page + 1)}
                                >
                                    {tA.next(lang)}
                                    <ChevronRight size={14} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    )

    if (embedded) {
        return (
            <>
                <div className="portal-audit__embedded-header">{totalBadge}</div>
                {content}
            </>
        )
    }

    return (
        <PageShell>
            {/* Header */}
            <PageHeader title={tA.title(lang)} actions={totalBadge} />
            {content}
        </PageShell>
    )
}
